import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { IApp } from '@core/models/app.interfaces';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { FieldType, FormlyModule } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';
import { FieldsetModule } from 'primeng/fieldset';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'formly-field-multi-checkbox',
  template: `
    <style>
      .inline {
        display: flex;
        flex-wrap: wrap;
      }
      .inline .p-field-checkbox {
        display: flex;
        flex: 0 0 20%;
        padding-right: 10px;
        padding-bottom: 15px;
      }
      .inline .p-field-checkbox label {
        right: 0;
        left: 20px;
        margin-top: 1px;
        margin-left: 0.5rem;
      }
    </style>
    <label *ngIf="props.dummyLabel" class="wcag-visuallyhidden" [for]="itemLabelPrefix + elementId">Label</label>
    <input type="text" class="wcag-visuallyhidden" [id]="itemLabelPrefix + elementId" tabindex="-1">
    <div [class.inline]="inline">
      <fieldset>
        <legend class="wcag-visuallyhidden">{{ itemLabelPrefix + elementId }}</legend>
      <div class="p-field-checkbox"
           *ngFor="let item of items">
        <p-checkbox [name]="elementId + '_checkbox_group'"
                    [value]="item.id"
                    [disabled]="item.disabled"
                    [formControl]="formControl"
                    [class.inline-checkbox]="inline"
                    [inputId]="itemLabelPrefix + elementId + '_item_' + item.id"
                    (onChange)="change(field, $event)">
        </p-checkbox>
        <label [for]="itemLabelPrefix + elementId + '_item_' + item.id" class="p-checkbox-label" [ngClass]="{ 'p-disabled': disabled }" [innerHTML]="item.name"></label>
          <ng-container *ngIf="item.value">
            <fa-icon
              pTooltip="{{ item.value }}"
              tooltipPosition="top"
              tabindex="0"
              [showOnFocus]="true"
              tooltipEvent="both"
              [escape]="false"
              class="mx-1"
              [icon]="['fas', 'info-circle']">
            </fa-icon>
          </ng-container>
      </div>
      </fieldset>
    </div>
  `,
  imports: [ CommonModule, ReactiveFormsModule, FormlyModule, TooltipModule, FontAwesomeModule, CheckboxModule,FieldsetModule
  ]
})
export class FormlyFieldMultiCheckboxComponent extends FieldType implements OnInit, OnDestroy {
  onDestroy$ = new Subject<void>();
  isLoading = false;
  items = [];
  source: Observable<IApp.IDict[]>;
  fluxSource: Observable<IApp.IFluxDict>;
  dataProvided: boolean;
  clearable: boolean;
  skipSort: boolean;
  public notFoundText: string;
  public placeholder: string;

  public translatePrefix;

  inline = false;

  constructor(
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.translatePrefix = this.props.translatePrefix ? `${ this.props.translatePrefix }.` : '';
    this.source = this.props.source;
    this.fluxSource = this.props.fluxSource;
    this.dataProvided = this.props.dataProvided;
    this.inline = this.props.inline;
    this.skipSort = this.props.skipSort || false;

    this.loadSource();
    this.translateService.onLangChange
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.loadSource());

    if (this.props.reload$) {
      this.props.reload$
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => this.loadSource());
    }
  }

  private loadSource() {
    if (this.fluxSource) {
      this.loadFluxData();
    } else if (this.dataProvided) {
      this.loadProvidedData();
    } else {
      this.loadData();
    }
  }

  get disabled() {
    return this.props.disabled;
  }

  get itemLabelPrefix(): string {
    return this.props.itemLabelPrefix ? `${this.props.itemLabelPrefix}_` : '';
  }

  get elementId(): string {
    if (this.field.id && this.field.id.includes('formly_')) {
      let prefix = this.form['id'];

      // check if in repeat section
      if (!prefix && this.field.parent.parent && this.field.parent.parent.parent && this.field.parent.parent.parent.type === 'repeat') {
        const cnt = this.field.parent.parent.key;
        prefix = `${this.field.parent.parent.parent['id']}_${cnt}`;
      }

      return `${prefix ? prefix + '_' : ''}${(this.field.key as string).replace('.', '_')}`;
    } else {
      return this.field.id;
    }
  }

  private loadData() {
    this.isLoading = true;
    this.source
    .pipe(
      takeUntil(this.onDestroy$),
      // first(),
      map((data: IApp.IDict[]) => {
        if (this.props.translateOptions) {
          const translated = data.map(el => ({
            ...el,
            name: this.translateService.instant(`${this.translatePrefix}${el[this.props.bindLabel || 'name']}`),
          }));
          return this.skipSort ? translated : translated.sort((a,b) => a.name.localeCompare(b.name));
        } else {
          return data;
        }
      }),
    )
      .subscribe(data => {
        this.items = [...data];
          if (this.field.key === 'accountId') {
            this.props.change && this.props.change(this.field, { id: this.model[this.field.key],
              'certificateNumber': this.model['certificateNumber'],
              initial: true });
          } else {
            this.props.change && this.props.change(this.field, { id: this.model[this.field.key as string], initial: true });
          }
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      },
        err => {
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        });
  }

  private loadFluxData() {
    this.isLoading = true;
    this.fluxSource
      .pipe(
        map((data: IApp.IFluxDict) => {
          if (this.props.translateOptions) {
            return data.roleDtos.map(el => ({
              ...el,
              name: this.translateService.instant(`${this.translatePrefix}${el[this.props.bindLabel || 'name']}`),
            }));
          } else {
            return data.roleDtos;
          }
        }),
      )
      .subscribe(data => {
      this.items = data;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    });
  }

  private loadProvidedData() {
    this.items = this.props.items;
  }

  get label() {
    let label = '';
    if (this.props.required) {
      label = label + 'Required field, ';
    }
    label += this.props.placeholder === 'selectItem' ? this.props.label : this.placeholder;
    return label;
  }

  change(field, data) {
    this.props.change && this.props.change(this.field, this.model[this.field.key as string]);
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
